/**
 * CertificateEditor
 * Modal component for generating new digital certificates
 */

import React, { useState } from 'react';
import { Button, Card, Form, Modal } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { validateCertificateGenerate } from '../../../common/spdCore/contexts/CertificateContext';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';

interface CertificateEditorProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

export const CertificateEditor: React.FC<CertificateEditorProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const spd = useSpdCore();
  const notifications = useNotifications();

  const [commonName, setCommonName] = useState('');
  const [password, setPassword] = useState('');
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const resetForm = () => {
    setCommonName('');
    setPassword('');
    setShowSaveErrors(false);
    setSaveErrors([]);
  };

  const onSave = async () => {
    try {
      const status = await validateCertificateGenerate({
        commonName,
        password,
      });
      if (!status.valid) {
        setSaveErrors(status.errors);
        setShowSaveErrors(true);
        return;
      }

      setIsSaving(true);
      const result = await spd.certificates.generate(commonName, password);

      // Download the generated certificate to disk
      if (result && result.pfx) {
        const binaryString = atob(result.pfx);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/x-pkcs12',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${commonName}.pfx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setShowSaveErrors(false);
      notifications.onPromise(Promise.resolve());
      resetForm();
      onHide();
      onSuccess?.();
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors([
        'Nie udało się wygenerować certyfikatu. ' + errors.join(' '),
      ]);
      setShowSaveErrors(true);
    } finally {
      setIsSaving(false);
    }
  };

  const onCancel = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Wygeneruj nowy certyfikat</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Header>
            <Card.Title className="mb-0">Parametry certyfikatu</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Common Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Np. example.com"
                  value={commonName}
                  onChange={e => setCommonName(e.target.value)}
                  disabled={isSaving}
                />
                <Form.Text className="text-muted">
                  Adres IP drukarki fiskalnej
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Hasło</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Wprowadź hasło do certyfikatu"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={isSaving}
                />
                <Form.Text className="text-muted">
                  Hasło będzie wymagane podczas importowania certyfikatu
                </Form.Text>
              </Form.Group>
            </Form>
          </Card.Body>
        </Card>

        {showSaveErrors && (
          <div className="mt-4">
            <ValidationAlert
              show={showSaveErrors}
              errors={saveErrors}
              onHide={() => setShowSaveErrors(false)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Generowanie...' : 'Wygeneruj certyfikat'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
